import { SoftButtonLink } from './Button';
import { cn } from '@/ui/design-system/utils/cn';
import { forwardRef } from 'react';

/**
 * The data-source tab bar: one segmented control, one tab per source.
 *
 * A tab is a link when the source can answer, and an inert label when it cannot
 * -- see DataSourceTabsByCoverage, which pairs the inert one with a tooltip.
 */
export const TabList = ({ children, className, ...props }) => (
    <div
        className={cn(
            'flex gap-0.5 rounded-md border border-brand-primary-light bg-card p-0.5',
            className,
        )}
        {...props}
    >
        {children}
    </div>
);

// Sizing is set here rather than taken from the button's own `size`, so every
// tab shape below measures the same whether it is a link or a span.
const TAB_BASE =
    'inline-flex h-auto min-w-[3em] items-center justify-center gap-1 rounded-sm px-4 py-2 text-sm font-medium leading-none';

// Both states must name a hover colour for text as well as background: the
// ghost button underneath declares its own, styled for a light background, and
// anything a tab leaves unset survives.
export const TabButtonLink = ({ intent, className, ...props }) => (
    <SoftButtonLink
        className={cn(
            TAB_BASE,
            intent === 'primary'
                ? 'bg-primary text-primary-foreground hover:bg-[var(--tab-selected-hover)] hover:text-primary-foreground'
                : 'bg-transparent hover:bg-accent hover:text-primary',
            className,
        )}
        {...props}
    />
);

/**
 * A tab for a source that does not cover what this page shows. Not a link, and
 * not a `disabled` button: a disabled control swallows the pointer events its
 * tooltip needs. It is focusable so the tooltip is reachable without a mouse,
 * since that tooltip carries the only explanation of why it cannot be opened.
 *
 * No tab role: this row is links that navigate, not a tablist, and one element
 * claiming to be a tab among them describes something that is not there.
 *
 * It still takes an `intent`, because the user can be looking at an uncovered
 * source: nothing redirects them away from one, so this is the tab that has to
 * show as current.
 */
export const TabButtonDisabled = forwardRef(function TabButtonDisabled(
    { children, className, intent, ...props },
    ref,
) {
    return (
        <span
            ref={ref}
            tabIndex={0}
            aria-disabled="true"
            {...(intent === 'primary' && { 'aria-current': 'page' })}
            className={cn(
                TAB_BASE,
                'cursor-not-allowed',
                intent === 'primary'
                    ? 'bg-muted text-muted-foreground'
                    : 'text-muted-foreground',
                className,
            )}
            {...props}
        >
            {children}
        </span>
    );
});

export function TabButtonBetaBadge({ intent }) {
    return (
        <span
            className={cn(
                '-my-2 inline-flex items-center gap-1 self-center rounded-full px-3 py-0.5 text-[0.5rem] font-semibold',
                intent === 'primary'
                    ? 'bg-primary-foreground/20 text-primary-foreground'
                    : 'bg-muted text-muted-foreground',
            )}
        >
            Beta
        </span>
    );
}
